import React, { useState, useRef, useEffect } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./CreateTemplatePopup.scss";
import { toast } from "react-hot-toast";

const CreateTemplatePopup = ({ isOpen, onClose, onSave, templateToEdit }) => {
	const [templateName, setTemplateName] = useState("");
	const [emailSubject, setEmailSubject] = useState("");
	const [emailBody, setEmailBody] = useState("");
	const [folder, setFolder] = useState("Capital Hub");
	const [visibility, setVisibility] = useState("Only You");
	const [pricingType, setPricingType] = useState("Free");
	const [saving, setSaving] = useState(false);
	const quillRef = useRef(null);

	// Set form values when editing an existing template
	useEffect(() => {
		if (templateToEdit) {
			setTemplateName(templateToEdit.templateName || "");
			setEmailSubject(templateToEdit.emailSubject || "");
			setEmailBody(templateToEdit.emailBody || "");
			setFolder(templateToEdit.folder || "Capital Hub");
			setVisibility(templateToEdit.visibility || "Only You");
			setPricingType(templateToEdit.pricingType || "Free");
		}
	}, [templateToEdit, isOpen]);

	// Reset form when closing
	useEffect(() => {
		if (!isOpen && !templateToEdit) {
			setTemplateName("");
			setEmailSubject("");
			setEmailBody("");
			setFolder("Capital Hub");
			setVisibility("Only You");
			setPricingType("Free");
		}
	}, [isOpen, templateToEdit]);

	const handleSave = async () => {
		const templateData = {
			templateName,
			emailSubject,
			emailBody,
			folder,
			visibility,
			pricingType,
		};

		try {
			setSaving(true);
			if (templateToEdit && templateToEdit._id) {
				// Update existing template
				await onSave(templateToEdit._id, templateData);
			} else {
				// Create new template
				await onSave(templateData);
			}
			onClose();
		} catch (error) {
			console.error("Error saving template:", error);
			toast.error("An error occurred while saving the template");
		} finally {
			setSaving(false);
		}
	};

	// React Quill toolbar modules with correct configuration
	const modules = {
		toolbar: [
			[{ header: [1, 2, false] }],
			["bold", "italic", "underline", "strike"],
			[
				{ list: "ordered" },
				{ list: "bullet" },
				{ indent: "-1" },
				{ indent: "+1" },
			],
			["link", "image", "code-block"],
			["clean"],
		],
	};

	// Add tooltips to toolbar buttons after the component mounts
	useEffect(() => {
		if (isOpen && quillRef.current) {
			const toolbar = quillRef.current.getEditor().getModule("toolbar");
			if (toolbar && toolbar.container) {
				const buttons = toolbar.container.querySelectorAll("button");
				const toolbarTooltips = {
					"ql-bold": "Bold",
					"ql-italic": "Italic",
					"ql-underline": "Underline",
					"ql-strike": "Strikethrough",
					"ql-link": "Insert Link",
					"ql-image": "Insert Image",
					"ql-code-block": "Insert Code Block",
					"ql-clean": "Clear Formatting",
					'ql-list[value="ordered"]': "Ordered List",
					'ql-list[value="bullet"]': "Bullet List",
					'ql-indent[value="-1"]': "Decrease Indent",
					'ql-indent[value="+1"]': "Increase Indent",
				};

				buttons.forEach((button) => {
					for (const className in toolbarTooltips) {
						if (button.classList.contains(className.split("[")[0])) {
							const selector = className.includes("[")
								? `${className.split("[")[0]}[${className.split("[")[1]}`
								: className;

							if (
								selector === 'ql-list[value="ordered"]' &&
								button.value === "ordered"
							) {
								button.setAttribute("title", toolbarTooltips[selector]);
							} else if (
								selector === 'ql-list[value="bullet"]' &&
								button.value === "bullet"
							) {
								button.setAttribute("title", toolbarTooltips[selector]);
							} else if (
								selector === 'ql-indent[value="-1"]' &&
								button.value === "-1"
							) {
								button.setAttribute("title", toolbarTooltips[selector]);
							} else if (
								selector === 'ql-indent[value="+1"]' &&
								button.value === "+1"
							) {
								button.setAttribute("title", toolbarTooltips[selector]);
							} else if (!selector.includes("[")) {
								button.setAttribute("title", toolbarTooltips[selector]);
							}
						}
					}
				});
			}
		}
	}, [isOpen]);

	// Conditional rendering to handle the popup visibility
	if (!isOpen) return null;

	return (
		<div className="popup-overlay">
			<div className="ct-template-popup">
				<div className="ct-popup-header">
					<h2>{templateToEdit ? "Edit template" : "Create new template"}</h2>
					<button className="close-button" onClick={onClose}>
						×
					</button>
				</div>

				<div className="ct-popup-content">
					<div className="form-group">
						<label htmlFor="templateName">Template Name</label>
						<input
							type="text"
							id="templateName"
							value={templateName}
							onChange={(e) => setTemplateName(e.target.value)}
							placeholder="Capital Hub"
						/>
					</div>

					<div className="form-group">
						<label htmlFor="emailSubject">Email Subject</label>
						<input
							type="text"
							id="emailSubject"
							value={emailSubject}
							onChange={(e) => setEmailSubject(e.target.value)}
							placeholder="Capital Hub"
						/>
					</div>

					<div className="form-group">
						<label htmlFor="emailBody">Email Body</label>
						<div className="quill-container">
							<ReactQuill
								ref={quillRef}
								theme="snow"
								value={emailBody}
								onChange={setEmailBody}
								modules={modules}
								className="quill-editor"
							/>
						</div>
					</div>

					<div className="form-group">
						<label htmlFor="folder">Folder</label>
						<div className="select-wrapper">
							<select
								id="folder"
								value={folder}
								onChange={(e) => setFolder(e.target.value)}
							>
								<option value="Capital Hub">Capital Hub</option>
								<option value="Marketing">Marketing</option>
								<option value="Sales">Sales</option>
								<option value="Support">Support</option>
							</select>
						</div>
					</div>

					<div className="form-row">
						<div className="form-group half-width">
							<label htmlFor="visibility">Visibility</label>
							<div className="select-wrapper">
								<select
									id="visibility"
									value={visibility}
									onChange={(e) => setVisibility(e.target.value)}
								>
									<option value="Only You">Only You</option>
									<option value="Team">Team</option>
									<option value="Public">Public</option>
								</select>
							</div>
						</div>

						<div className="form-group half-width">
							<label htmlFor="pricingType">Free or Paid</label>
							<div className="select-wrapper">
								<select
									id="pricingType"
									value={pricingType}
									onChange={(e) => setPricingType(e.target.value)}
								>
									<option value="Free">Free</option>
									<option value="Paid">Paid</option>
								</select>
							</div>
						</div>
					</div>
				</div>

				<div className="ct-popup-footer">
					<button 
						className="save-button" 
						onClick={handleSave}
						disabled={saving}
					>
						{saving ? (
							<>
								<span className="button-loader"></span>
								{templateToEdit ? "Updating..." : "Saving..."}
							</>
						) : (
							templateToEdit ? "Update Template" : "Save Template"
						)}
					</button>
				</div>
			</div>
		</div>
	);
};

export default CreateTemplatePopup;
